import React, { useCallback, memo } from "react";
import DataGrid, {
    Column,
    ColumnChooser,
    ColumnChooserSearch,
    Export,
    FilterRow,
    Paging,
    Scrolling,
    Selection,
    Sorting,
    StateStoring,
    Toolbar,
    Item,
} from "devextreme-react/data-grid";

import GridLayoutManager from "../../../../gridLayoutCustomization/GridLayoutManager";
import { GridLayout } from "../../../../common/GridLayout";

/**
 * Read-only result grid for the WM charges operation screens. Currently used only by Update Free Days
 * and Grace Periods (WMD0012).
 *
 * Calculate Deferred Handling Revenue keeps its own DeferredRevenueResultGrid, which this component was
 * generalised from. Folding that screen onto this one would have meant an untested change to a shipped
 * screen — extra grid options, memoisation — for no functional gain, so the two are deliberately left
 * separate. The next charges operation to be migrated should adopt THIS component, and that is the point
 * at which collapsing the two is worth doing together.
 *
 * These grids show query/stored-procedure output, not editable entity rows, so there is deliberately
 * no <Editing>, no CustomStore and no concurrency store — Architecture.md rules 4-6, 14-16 do not
 * apply.
 *
 * keyExpr is the caller's choice: pass the real key when the rows have one (a ROWID), or a synthetic
 * index stamped by the page when they do not — stored-procedure aggregates often have no unique tuple
 * at all, and a duplicate key makes DevExtreme throw or drop rows.
 *
 * COLUMN WIDTH: columnAutoWidth sizes columns to content, and columns carrying a `minWidth` are never
 * squeezed below it — so once the total exceeds the container the grid scrolls horizontally instead of
 * compressing every column to an unreadable sliver. A caller with many columns should set minWidth on
 * all of them. Do not wrap this grid in a ScrollView: the grid owns both scroll axes, and an outer
 * scroller gives it an unbounded width to lay out against, which defeats the horizontal scrollbar.
 *
 * LAYOUT PERSISTENCE is opt-in. Pass `gridKey` (plus `currentLayout` / `setCurrentLayout` /
 * `accessToken`) and the grid gains the standard saved-layout dropdown and column-state persistence,
 * exactly as the search grids have. Omit `gridKey` and neither is rendered.
 */
const OperationResultGrid = ({
    rows,
    columns,
    keyExpr,
    noDataText,
    toolbarAction,
    height = "calc(100vh - 260px)",
    gridRef,
    gridKey,
    currentLayout,
    setCurrentLayout,
    accessToken,
}) => {
    const layoutEnabled = Boolean(gridKey && gridRef);

    const onExporting = (e) => {
        if (window.handleDevExtremeDataGridExport) {
            window.handleDevExtremeDataGridExport(e);
        }
    };

    const loadState = useCallback(
        () => GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout),
        [gridKey, accessToken, currentLayout]
    );

    const saveState = useCallback(
        (state) => GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout),
        [gridKey, accessToken, currentLayout]
    );

    return (
        <DataGrid
            ref={gridRef}
            dataSource={rows || []}
            keyExpr={keyExpr}
            showBorders={true}
            rowAlternationEnabled={true}
            allowColumnReordering={true}
            allowColumnResizing={true}
            // "widget" keeps the neighbouring columns' widths when one is resized, so a wide grid stays
            // wide and scrolls rather than redistributing the space.
            columnResizingMode="widget"
            columnAutoWidth={true}
            hoverStateEnabled={true}
            noDataText={noDataText}
            className="search-page-grid"
            height={height}
            onExporting={onExporting}
        >
            <Selection mode="single" />
            <Sorting mode="multiple" />
            <FilterRow visible={true} />
            {/* columnRenderingMode virtual keeps a wide column set responsive while scrolling. */}
            <Scrolling mode="virtual" rowRenderingMode="virtual" columnRenderingMode="virtual" />
            <Paging enabled={true} pageSize={50} />
            <Export enabled={true} />
            <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: "Search column" }} />
            </ColumnChooser>

            {/*
                Rendered unconditionally and gated with `enabled`, NOT wrapped in `layoutEnabled && …`.
                devextreme-react builds the widget's option tree from the static child structure, so a
                conditionally-rendered config component does not reliably get applied — the symptom was
                customLoad never running, i.e. no GetGridLayout request and saved layouts silently
                ignored while the layout dropdown itself still worked.
            */}
            <StateStoring
                enabled={layoutEnabled}
                type="custom"
                storageKey={gridKey}
                customLoad={loadState}
                customSave={saveState}
            />

            {columns.map((column) => (
                <Column key={column.dataField} {...column} />
            ))}

            <Toolbar>
                {/* Right-aligned; declared first so it sits left of the export/column-chooser icons. */}
                {toolbarAction && <Item location="after" render={toolbarAction} />}
                {layoutEnabled && (
                    <Item location="after">
                        <GridLayoutManager
                            gridKey={gridKey}
                            dataGrid={gridRef}
                            currentLayout={currentLayout}
                            setCurrentLayout={setCurrentLayout}
                            accessToken={accessToken}
                        />
                    </Item>
                )}
                <Item name="exportButton" />
                <Item name="columnChooserButton" />
            </Toolbar>
        </DataGrid>
    );
};

/**
 * Memoised deliberately, and it is load-bearing rather than an optimisation.
 *
 * devextreme-react rebuilds a DataGrid's toolbar templates whenever the grid re-renders, which unmounts
 * and remounts whatever those templates contain (GridLayoutManager says as much in its own source).
 * GridLayoutManager fetches the saved-layout list on mount, so every incidental re-render of the host
 * page — a spinner flag flipping, an unrelated piece of state — fired another GetGridLayouts request.
 *
 * All of this component's props are stable references (module-level column arrays, a ref, setState
 * functions, literals), so a shallow compare stops those re-renders at the boundary. Keep them stable:
 * passing a freshly-built array or an inline arrow for `columns` or `toolbarAction` silently defeats
 * this and the duplicate requests come back.
 */
export default memo(OperationResultGrid);
